import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Box,
  Tooltip,
  CardContent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';

const TABLE_HEAD = [
  { id: 'client', label: 'Client', alignRight: false },
  { id: 'employee', label: 'Assigned Employee', alignRight: false },
  { id: 'assignment_date', label: 'Assignment Date', alignRight: false },
  { id: 'status', label: 'Client Status', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: true },
];

const AssignmentTable = ({
  assignments,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onEditAssignment,
  onUnassignClient,
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" mb={2}>
          Active Client Assignments
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map((headCell) => (
                  <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : assignments?.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {isLoading ? 'Loading assignments...' : 'No active assignments found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow hover key={assignment.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{assignment.practice_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {assignment.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{assignment.assigned_employee?.full_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {assignment.assigned_employee?.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {assignment.assignment_date ? new Date(assignment.assignment_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.status}
                        size="small"
                        color={
                          assignment.status === 'Active'
                            ? 'success'
                            : assignment.status === 'Locked'
                            ? 'error'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Edit Assignment">
                          <IconButton
                            size="small"
                            onClick={() => onEditAssignment(assignment)}
                            sx={{ color: 'primary.main' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Unassign">
                          <IconButton
                            size="small"
                            onClick={() => onUnassignClient(assignment)}
                            sx={{ color: 'error.main' }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page - 1} // Material-UI uses 0-based indexing
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </CardContent>
    </Card>
  );
};

AssignmentTable.propTypes = {
  assignments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      practice_name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      assigned_employee: PropTypes.shape({
        full_name: PropTypes.string,
        email: PropTypes.string,
      }),
      assignment_date: PropTypes.string,
      status: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onEditAssignment: PropTypes.func.isRequired,
  onUnassignClient: PropTypes.func.isRequired,
};

export default AssignmentTable;
